import {
  normalizePayloadValue,
  UPLOAD_PROGRESS_MAX,
} from 'helpers/publishProgressUtils';
import {
  submitPublishRequest,
  uploadPublishImage,
  uploadPublishImageAsset,
} from 'helpers/publishJobUtils';

export const runBannerPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const editing = job?.mode === 'EDIT' && job?.recordId;
  const needsWebUpload = Boolean(files?.webFile);
  const needsMobileUpload = Boolean(files?.mobileFile);

  if (!editing && (!needsWebUpload || !needsMobileUpload)) {
    throw new Error('Both web image and mobile image are required.');
  }

  const webImage = await uploadPublishImage({
    file: files?.webFile,
    existingUrl: files?.existingWebImage || job.payload?.webImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-web-image',
    rangeStart: 0,
    rangeEnd: needsMobileUpload ? 40 : UPLOAD_PROGRESS_MAX,
  });

  const mobileImage = await uploadPublishImage({
    file: files?.mobileFile,
    existingUrl: files?.existingMobileImage || job.payload?.mobileImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-mobile-image',
    rangeStart: needsWebUpload ? 40 : 0,
    rangeEnd: UPLOAD_PROGRESS_MAX,
  });

  if (!webImage || !mobileImage) {
    throw new Error('Both web image and mobile image are required.');
  }

  return submitPublishRequest({
    job,
    createPath: '/banner/create',
    updatePath: (id) => `/banner/update/${id}`,
    payload: {
      ...job.payload,
      webImage,
      mobileImage,
      title: normalizePayloadValue(job.payload?.title ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'banner',
  });
};

export const runTemplatePublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  let uploadPercent = 0;

  const fcmImage = await uploadPublishImage({
    file: files?.fcmFile,
    existingUrl: files?.existingFcmImage || payload?.fcm?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-fcm-image',
    rangeStart: 0,
    rangeEnd: files?.whatsappFile ? 42 : UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (fcmImage) {
    payload.fcm = {
      ...payload.fcm,
      image: fcmImage,
    };
    uploadPercent = files?.whatsappFile ? 42 : UPLOAD_PROGRESS_MAX;
  }

  const whatsappImage = await uploadPublishImage({
    file: files?.whatsappFile,
    existingUrl: files?.existingWhatsappImage || payload?.whatsapp?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-whatsapp-image',
    rangeStart: uploadPercent,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (whatsappImage) {
    payload.whatsapp = {
      ...payload.whatsapp,
      image: whatsappImage,
    };
    uploadPercent = UPLOAD_PROGRESS_MAX;
  }

  return submitPublishRequest({
    job,
    createPath: '/template/create/admin',
    updatePath: (id) => `/template/update/admin/${id}`,
    payload: {
      ...payload,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'template',
    uploadPercent: uploadPercent || UPLOAD_PROGRESS_MAX,
  });
};

export const runPartnersPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };

  const image = await uploadPublishImage({
    file: files?.iconFile, // from storeJobFiles
    existingUrl: files?.existingIcon || payload?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-image',
    rangeStart: 0,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (!image) {
    throw new Error('Image is required.');
  }

  payload.image = image;

  return submitPublishRequest({
    job,
    createPath: '/partners/admin/create',
    updatePath: (id) => `/partners/admin/update/${id}`,
    payload: {
      ...payload,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'partner',
    uploadPercent: UPLOAD_PROGRESS_MAX,
    updateMethod: 'PATCH',
  });
};

export const runBrandsPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };

  const image = await uploadPublishImage({
    file: files?.iconFile, // from storeJobFiles
    existingUrl: files?.existingIcon || payload?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-image',
    rangeStart: 0,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (!image) {
    throw new Error('Image is required.');
  }

  payload.image = image;

  return submitPublishRequest({
    job,
    createPath: '/brands/admin/create',
    updatePath: (id) => `/brands/admin/update/${id}`,
    payload: {
      ...payload,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'brand',
    uploadPercent: UPLOAD_PROGRESS_MAX,
    updateMethod: 'PATCH',
  });
};

export const runDepartmentsPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };

  const icon = await uploadPublishImage({
    file: files?.iconFile,
    existingUrl: files?.existingIcon || payload?.icon,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-image',
    rangeStart: 0,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (!icon) {
    throw new Error('Icon is required.');
  }

  payload.icon = icon;

  return submitPublishRequest({
    job,
    createPath: '/departments/admin/create',
    updatePath: (id) => `/departments/admin/update/${id}`,
    payload: {
      ...payload,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'department',
    uploadPercent: UPLOAD_PROGRESS_MAX,
    updateMethod: 'PATCH',
  });
};

export const runCareersPublishJob = async ({
  job,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };

  return submitPublishRequest({
    job,
    createPath: '/careers/admin/create',
    updatePath: (id) => `/careers/admin/update/${id}`,
    payload: {
      ...payload,
      title: normalizePayloadValue(payload.title ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'career',
    uploadPercent: 0,
  });
};

export const runCareerApplicationsPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };

  const resume = await uploadPublishImage({
    file: files?.resumeFile,
    existingUrl: files?.existingResume || payload?.resume,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-resume',
    rangeStart: 0,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (!resume) {
    throw new Error('Resume is required.');
  }

  payload.resume = resume;

  return submitPublishRequest({
    job,
    createPath: '/career-application/admin/create',
    updatePath: (id) => `/career-application/admin/update/${id}`,
    payload: {
      ...payload,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'career application',
    uploadPercent: UPLOAD_PROGRESS_MAX,
  });
};

export const runPagesPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const imageItems = Array.isArray(files?.imageItems) ? files.imageItems : [];
  const fileItems = Array.isArray(files?.fileItems) ? files.fileItems : [];
  const totalUploads = imageItems.length + fileItems.length;

  if (!imageItems.length) {
    throw new Error('Add atleast one image');
  }

  if (!imageItems.some((item) => item?.mainImage === 'Yes')) {
    throw new Error('Select a main image from images!');
  }

  const uploadedImages = [];
  let uploadCursor = 0;

  for (let i = 0; i < imageItems.length; i += 1) {
    const item = imageItems[i];
    const rangeStart =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : 0;
    uploadCursor += 1;
    const rangeEnd =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : UPLOAD_PROGRESS_MAX;

    const uploaded = await uploadPublishImageAsset({
      file: item?.file,
      existingUrl: item?.existingUrl,
      existingThumbnail: item?.existingThumbnail,
      accessToken,
      signal,
      onProgress,
      phase: 'uploading-image',
      rangeStart,
      rangeEnd,
      thumbnailRequired: 'Yes',
    });

    if (!uploaded?.url) {
      throw new Error('Image upload failed');
    }

    if (!uploaded?.thumbnail) {
      throw new Error('Image thumbnail is required');
    }

    uploadedImages.push({
      url: uploaded.url,
      thumbnail: uploaded.thumbnail,
      mainImage: item?.mainImage,
      imageTag: item?.imageTag,
    });
  }

  const uploadedFiles = [];
  for (let i = 0; i < fileItems.length; i += 1) {
    const item = fileItems[i];
    const rangeStart =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : 0;
    uploadCursor += 1;
    const rangeEnd =
      totalUploads > 0
        ? Math.round((uploadCursor / totalUploads) * UPLOAD_PROGRESS_MAX)
        : UPLOAD_PROGRESS_MAX;

    const uploaded = await uploadPublishImageAsset({
      file: item?.file,
      existingUrl: item?.existingUrl,
      accessToken,
      signal,
      onProgress,
      phase: 'uploading-file',
      rangeStart,
      rangeEnd,
      thumbnailRequired: 'No',
    });

    if (uploaded?.url) {
      uploadedFiles.push(uploaded.url);
    }
  }

  return submitPublishRequest({
    job,
    createPath: '/Pages/create',
    updatePath: (id) => `/Pages/update/${id}`,
    payload: {
      ...payload,
      images: uploadedImages,
      files: uploadedFiles,
      name: normalizePayloadValue(payload.name ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel: 'page',
    uploadPercent: UPLOAD_PROGRESS_MAX,
  });
};

const runImageEntityPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
  createPath,
  updatePath,
  entityLabel,
  titleField = 'name',
  imageRequired = true,
}) => {
  const payload = { ...job.payload };

  const image = await uploadPublishImage({
    file: files?.iconFile,
    existingUrl: files?.existingIcon || payload?.image,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-image',
    rangeStart: 0,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (imageRequired && !image) {
    throw new Error('Image is required.');
  }

  if (image) {
    payload.image = image;
  } else if (imageRequired === false) {
    payload.image = 'no-image';
  } else {
    delete payload.image;
  }

  return submitPublishRequest({
    job,
    createPath,
    updatePath,
    payload: {
      ...payload,
      [titleField]: normalizePayloadValue(payload[titleField] ?? job.title),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel,
    uploadPercent: UPLOAD_PROGRESS_MAX,
  });
};

export const runOurTeamPublishJob = (params) =>
  runImageEntityPublishJob({
    ...params,
    createPath: '/our-team/admin/create',
    updatePath: (id) => `/our-team/admin/update/${id}`,
    entityLabel: 'team member',
    titleField: 'name',
    imageRequired: false,
  });

export const runProjectsPublishJob = (params) =>
  runImageEntityPublishJob({
    ...params,
    createPath: '/projects/admin/create',
    updatePath: (id) => `/projects/admin/update/${id}`,
    entityLabel: 'project',
    titleField: 'title',
  });

const runSimpleEntityPublishJob = async ({
  job,
  accessToken,
  onProgress,
  signal,
  createPath,
  updatePath,
  entityLabel,
  titleField = 'name',
  updateMethod = 'PUT',
}) => {
  const payload = { ...job.payload };

  return submitPublishRequest({
    job,
    createPath,
    updatePath,
    payload: {
      ...payload,
      ...(titleField && payload[titleField] !== undefined
        ? { [titleField]: normalizePayloadValue(payload[titleField] ?? job.title) }
        : {}),
    },
    accessToken,
    signal,
    onProgress,
    entityLabel,
    uploadPercent: 0,
    updateMethod,
  });
};

export const runCategoriesPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/pagesCategory/create',
    updatePath: (id) => `/pagesCategory/update/${id}`,
    entityLabel: 'category',
    titleField: 'name',
  });

export const runWidgetsPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/widgets/create/admin',
    updatePath: (id) => `/widgets/update/admin/${id}`,
    entityLabel: 'widget',
    titleField: 'title',
  });

export const runSeoPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/pages-seo/create',
    updatePath: (id) => `/pages-seo/update/${id}`,
    entityLabel: 'SEO entry',
    titleField: 'metatitle',
  });

export const runInformationPolicyPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/information-policy/add',
    updatePath: (id) => `/information-policy/update/${id}`,
    entityLabel: 'information policy',
    titleField: null,
    updateMethod: 'PATCH',
  });

export const runConfigSettingsPublishJob = (params) =>
  runSimpleEntityPublishJob({
    ...params,
    createPath: '/config-settings/update/admin',
    updatePath: (id) => `/config-settings/update/admin/${id}`,
    entityLabel: 'config settings',
    titleField: null,
    updateMethod: 'PUT',
  });

export const runAdminSettingsPublishJob = async ({
  job,
  files,
  accessToken,
  onProgress,
  signal,
}) => {
  const payload = { ...job.payload };
  const hasFavicon = Boolean(files?.faviconFile || files?.existingFavicon);

  const logo = await uploadPublishImage({
    file: files?.logoFile,
    existingUrl: files?.existingLogo || payload?.logo,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-logo',
    rangeStart: 0,
    rangeEnd: hasFavicon ? 50 : UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (logo) {
    payload.logo = logo;
  }

  const favIconImage = await uploadPublishImage({
    file: files?.faviconFile,
    existingUrl: files?.existingFavicon || payload?.favIconImage,
    accessToken,
    signal,
    onProgress,
    phase: 'uploading-favicon',
    rangeStart: hasFavicon ? 50 : UPLOAD_PROGRESS_MAX,
    rangeEnd: UPLOAD_PROGRESS_MAX,
    thumbnailRequired: 'No',
  });

  if (favIconImage) {
    payload.favIconImage = favIconImage;
  }

  return submitPublishRequest({
    job,
    createPath: '/AdminSettings/update/admin',
    updatePath: (id) => `/AdminSettings/update/admin/${id}`,
    payload,
    accessToken,
    signal,
    onProgress,
    entityLabel: 'settings',
    uploadPercent: UPLOAD_PROGRESS_MAX,
    updateMethod: 'PATCH',
  });
};
